import { put, takeLatest, call } from "redux-saga/effects";
import {
	setListAppointment,
	setDetailAppointment,
	GetListAppointmentAsyncPayload,
	getListAppointmentAsync,
	GetDetailAppointmentAsyncPayload,
	getDetailAppointmentAsync,
	UpdateAppointmentAsyncPayload,
	updateAppointmentAsync,
	addExpenseAsync,
	AddExpenseAsyncPayload,
	updateExpenseAsync,
	UpdateExpenseAsyncPayload,
	DeleteExpenseAsyncPayload,
	deleteExpenseAsync,
	setLoadingAppointment,
	UpdateStatusBookingCarAsyncPayload,
	UpdateStatusBookingCarAsync,
	setStatusBookingCar,
	AddServiceAsyncPayload,
	addServiceAsync,
	DeleteServiceAsyncPayload,
	deleteServiceAsync,
} from "./appointment.slice";
import { message } from "antd";
import api from "service/apiService";
import { t } from "@lingui/macro";
import { translate } from "utils";
import { PayloadAction } from "@reduxjs/toolkit";
import { ApiResponse } from "types";
import { Appointment } from "../types";

// Get list
function* getListAppointmentSaga(
	action: PayloadAction<GetListAppointmentAsyncPayload>,
) {
	const { query, callback } = action.payload;

	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get("/dashboard/bookings", {
				...query,
				skip: query.skip * query.take,
			});
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setListAppointment.type, payload: response });
	} else {
		message.error(translate(error.message));
	}

	callback && callback();
}
function* watchGetListAppointment() {
	yield takeLatest(getListAppointmentAsync, getListAppointmentSaga);
}

// Detail
function* getDetailAppointmentSaga(
	action: PayloadAction<GetDetailAppointmentAsyncPayload>,
) {
	const { id } = action.payload;
	yield put({ type: setLoadingAppointment.type, payload: true });
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<Appointment>>(
				`/dashboard/bookings/${id}`,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		let appointmentDetail = response.data as Appointment;
		appointmentDetail = {
			...appointmentDetail,
			bookingServices: (appointmentDetail.bookingServices || []).filter(
				el => el.status !== "canceled",
			),
		};

		yield put({
			type: setDetailAppointment.type,
			payload: appointmentDetail,
		});
	} else {
		message.error(translate(error.message));
	}
	yield put({ type: setLoadingAppointment.type, payload: false });
}
function* watchGetDetailAppointment() {
	yield takeLatest(getDetailAppointmentAsync, getDetailAppointmentSaga);
}

//update status BookingCar
function* updateStatusBookingCarSaga(
	action: PayloadAction<UpdateStatusBookingCarAsyncPayload>,
) {
	const { id, status, language } = action.payload;
	yield put({ type: setLoadingAppointment.type, payload: true });
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.patch(
				`/dashboard/booking-cars/${id}`,
				{
					status,
				},
				{
					language,
				},
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setStatusBookingCar.type, payload: response });
		yield put({ type: setLoadingAppointment.type, payload: false });
		message.success(t`Update successful`);
	}
}
function* watchUpdateStatusBookingCar() {
	yield takeLatest(UpdateStatusBookingCarAsync, updateStatusBookingCarSaga);
}

// Update
function* updateAppointmentSaga(
	action: PayloadAction<UpdateAppointmentAsyncPayload>,
) {
	const { id, data, getResult } = action.payload;
	yield put({ type: setLoadingAppointment.type, payload: true });
	const { response, error } = yield call(async () => {
		try {
			const response = await api.put(`/dashboard/bookings/${id}`, data);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({
			type: setLoadingAppointment.type,
			payload: false,
		});
		message.success(t`Update successful`);
		getResult(true);
	} else {
		message.error(translate(error.message));
		getResult(false);
	}
}
function* watchUpdateAppointment() {
	yield takeLatest(updateAppointmentAsync, updateAppointmentSaga);
}

// Add expense
function* addExpenseSaga(action: PayloadAction<AddExpenseAsyncPayload>) {
	const { bookId, data, getResult } = action.payload;
	yield put({ type: setLoadingAppointment.type, payload: true });
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.post(
				`/dashboard/bookings/${bookId}/expenses`,
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(t`Create successful`);
		getResult({
			success: true,
			id: response.data.id,
		});
	} else {
		message.error(translate(error.message));
		getResult({
			success: false,
			id: 0,
		});
	}
	yield put({ type: setLoadingAppointment.type, payload: false });
}
function* watchAddExpense() {
	yield takeLatest(addExpenseAsync, addExpenseSaga);
}

// Update expense
function* updateExpenseSaga(action: PayloadAction<UpdateExpenseAsyncPayload>) {
	const { bookId, id, data, getResult } = action.payload;
	yield put({ type: setLoadingAppointment.type, payload: true });
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.put(
				`/dashboard/bookings/${bookId}/expenses/${id}`,
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(t`Update successful`);
		getResult(true);
	} else {
		message.error(translate(error.message));
		getResult(false);
	}
	yield put({ type: setLoadingAppointment.type, payload: false });
}
function* watchUpdateExpense() {
	yield takeLatest(updateExpenseAsync, updateExpenseSaga);
}

// Delete expense
function* deleteExpenseSaga(action: PayloadAction<DeleteExpenseAsyncPayload>) {
	const { bookId, id, getResult } = action.payload;
	yield put({ type: setLoadingAppointment.type, payload: true });
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.delete(
				`/dashboard/bookings/${bookId}/expenses/${id}`,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(t`Delete successful`);
		getResult(true);
	} else {
		message.error(translate(error.message));
	}
	yield put({ type: setLoadingAppointment.type, payload: false });
}
function* watchDeleteExpense() {
	yield takeLatest(deleteExpenseAsync, deleteExpenseSaga);
}

// Add service
function* addServiceSaga(action: PayloadAction<AddServiceAsyncPayload>) {
	const { bookId, data, getResult } = action.payload;
	yield put({ type: setLoadingAppointment.type, payload: true });
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.post(
				`/dashboard/bookings/${bookId}/services`,
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(t`Create successful`);
		yield put({
			type: getDetailAppointmentAsync.type,
			payload: { id: bookId },
		});
		getResult({
			success: true,
		});
	} else {
		message.error(translate(error.message));
		getResult({
			success: false,
		});
	}
	yield put({ type: setLoadingAppointment.type, payload: false });
}
function* watchAddService() {
	yield takeLatest(addServiceAsync, addServiceSaga);
}

// Delete service
function* deleteServiceSaga(action: PayloadAction<DeleteServiceAsyncPayload>) {
	const { bookId, serviceId, getResult } = action.payload;
	yield put({ type: setLoadingAppointment.type, payload: true });
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.delete(
				`/dashboard/bookings/${bookId}/services/${serviceId}`,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(t`Delete successful`);
		yield put({
			type: getDetailAppointmentAsync.type,
			payload: { id: bookId },
		});
		getResult({ success: true });
	} else {
		message.error(translate(error.message));
	}
	yield put({ type: setLoadingAppointment.type, payload: false });
}
function* watchDeleteService() {
	yield takeLatest(deleteServiceAsync, deleteServiceSaga);
}

const customerSaga = [
	watchGetListAppointment(),
	watchGetDetailAppointment(),
	watchUpdateAppointment(),
	watchAddExpense(),
	watchUpdateExpense(),
	watchDeleteExpense(),
	watchUpdateStatusBookingCar(),
	watchAddService(),
	watchDeleteService(),
];
export default customerSaga;
